import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, RotateCcw, Save } from 'lucide-react';
import { Modal } from '../../components/ui';
import { textosPadraoApi } from '../../services/api';
import { useAuthStore } from '../../store/useAuthStore';
import { mensagemDoErro } from '../../utils/erroDaApi';

// Textos que o redator não escreve: seções fixas e o quadro de parâmetros das horas extras.
// O Dr. Lucas publica pelo Claude ou aqui; a Dra. Érica acompanha o histórico (decisão de
// 23/09/2026). Cada publicação é uma versão; reverter publica de novo uma versão antiga.

const PODE_PUBLICAR = ['juris:manage', 'settings:templates'];

function formatarData(iso) {
  if (!iso) return 'texto do código';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
}

function nomeVariante(v) {
  const partes = [];
  if (v.regime && v.regime !== '*') partes.push(`regime ${v.regime}`);
  if (v.tribunal && v.tribunal !== '*') partes.push(v.tribunal);
  return partes.length ? partes.join(' · ') : 'geral';
}

function Blocos({ versao }) {
  if (!versao) return <p className="tp-vazio">—</p>;
  return (
    <div className="tp-blocos">
      {versao.titulo && <p className="tp-titulo">{versao.titulo}</p>}
      {(versao.blocos || []).map((b, i) => <p key={i}>{b}</p>)}
    </div>
  );
}

export default function TextosPadrao() {
  const queryClient = useQueryClient();
  const permissoes = useAuthStore(s => s.user?.permissions || []);
  const podePublicar = PODE_PUBLICAR.some(p => permissoes.includes(p));
  const [sel, setSel] = useState({ chave: null, regime: '*', tribunal: '*' });
  const [comparar, setComparar] = useState(null);
  const [edicao, setEdicao] = useState(null);
  const [reversao, setReversao] = useState(null);

  const lista = useQuery({ queryKey: ['textos-padrao'], queryFn: textosPadraoApi.list });
  const chave = sel.chave || lista.data?.textos?.[0]?.chave;
  const detalhe = useQuery({
    queryKey: ['textos-padrao', chave, sel.regime, sel.tribunal],
    queryFn: () => textosPadraoApi.get(chave, { regime: sel.regime, tribunal: sel.tribunal }),
    enabled: Boolean(chave),
  });

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['textos-padrao'] });
  // O 422 do Pydantic vem como lista; o do serviço, como texto. Os dois viram frase legível.
  const erro = (e) => toast.error(mensagemDoErro(e, 'Não foi possível salvar: confira o título, o texto e o motivo.'));
  const publicar = useMutation({
    mutationFn: (dados) => textosPadraoApi.publicar(chave, dados),
    onSuccess: (r) => { toast.success(`Versão ${r.versao} publicada.`); setEdicao(null); recarregar(); },
    onError: erro,
  });
  const reverter = useMutation({
    mutationFn: (dados) => textosPadraoApi.reverter(chave, dados),
    onSuccess: (r) => { toast.success(`Revertido: versão ${r.versao} publicada.`); setReversao(null); recarregar(); },
    onError: erro,
  });

  const textoAtual = useMemo(() => (lista.data?.textos || []).find(t => t.chave === chave), [lista.data, chave]);
  const historico = detalhe.data?.historico || [];

  if (lista.isLoading) return <p className="tp-vazio">Carregando…</p>;
  if (lista.isError) return <p className="tp-vazio">Não foi possível carregar os textos padrão.</p>;

  return (
    <div className="tp">
      <div className="tp-cabecalho">
        <h2>Textos do escritório</h2>
        <p>Seções fixas e quadro de parâmetros que toda peça traz. Cada mudança fica aqui, com quem publicou, quando e por quê.</p>
      </div>

      <div className="tp-grade">
        <nav className="tp-lista" aria-label="Textos padrão">
          {(lista.data?.textos || []).map(t => (
            <div key={t.chave} className="tp-item">
              {t.variantes.map(v => {
                const ativo = t.chave === chave && v.regime === sel.regime && v.tribunal === sel.tribunal;
                return (
                  <button key={`${v.regime}-${v.tribunal}`} type="button"
                    className={`tp-botao${ativo ? ' tp-botao-ativo' : ''}`}
                    onClick={() => { setSel({ chave: t.chave, regime: v.regime, tribunal: v.tribunal }); setComparar(null); }}>
                    <span className="tp-rotulo">{t.rotulo}</span>
                    <span className="tp-meta">{nomeVariante(v)} · versão {v.versao}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <section className="tp-detalhe">
          {detalhe.isLoading && <p className="tp-vazio">Carregando…</p>}
          {detalhe.data && (
            <>
              <div className="tp-barra">
                <div>
                  <strong>{textoAtual?.rotulo}</strong> <span className="tp-meta">({nomeVariante(sel)})</span>
                </div>
                {podePublicar && (
                  <button type="button" className="btn btn-primary btn-sm"
                    onClick={() => setEdicao({
                      titulo: detalhe.data.vigente?.titulo || '',
                      texto: (detalhe.data.vigente?.blocos || []).join('\n\n'),
                      motivo: '',
                    })}>
                    <Save size={13} /> Publicar nova versão
                  </button>
                )}
              </div>
              <h3 className="tp-subtitulo">Em vigor — versão {detalhe.data.vigente?.versao}</h3>
              <Blocos versao={detalhe.data.vigente} />

              <h3 className="tp-subtitulo"><History size={14} /> Histórico</h3>
              <ol className="tp-historico">
                {historico.map((h, i) => (
                  <li key={h.versao} className="tp-versao">
                    <div className="tp-versao-cabecalho">
                      <span><strong>v{h.versao}</strong> · {formatarData(h.criado_em)} · {h.criado_por_nome || (h.origem === 'codigo' ? 'código' : '—')}</span>
                      <span className="tp-acoes">
                        {historico[i + 1] && (
                          <button type="button" className="btn btn-ghost btn-sm"
                            onClick={() => setComparar(comparar === h.versao ? null : h.versao)}>
                            {comparar === h.versao ? 'Fechar' : 'Antes e depois'}
                          </button>
                        )}
                        {podePublicar && i > 0 && !h.removida && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setReversao({ versao: h.versao, motivo: '' })}>
                            <RotateCcw size={12} /> Reverter para esta
                          </button>
                        )}
                      </span>
                    </div>
                    <p className="tp-motivo">{h.removida ? 'Variante retirada. ' : ''}{h.motivo}</p>
                    {comparar === h.versao && (
                      <div className="tp-comparacao">
                        <div><h4>Antes (v{historico[i + 1].versao})</h4><Blocos versao={historico[i + 1]} /></div>
                        <div><h4>Depois (v{h.versao})</h4><Blocos versao={h} /></div>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </>
          )}
        </section>
      </div>

      <Modal isOpen={Boolean(edicao)} onClose={() => setEdicao(null)} title="Publicar nova versão" width={720}
        footer={(
          <div className="tp-rodape">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEdicao(null)}>Cancelar</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={publicar.isPending}
              onClick={() => publicar.mutate({
                titulo: edicao.titulo || null,
                blocos: edicao.texto.split(/\n\s*\n/).map(b => b.trim()).filter(Boolean),
                motivo: edicao.motivo, regime: sel.regime, tribunal: sel.tribunal,
              })}>
              Publicar
            </button>
          </div>
        )}>
        {edicao && (
          <div className="tp-form">
            {textoAtual?.tipo === 'secao' && (
              <label>Título
                <input className="input" value={edicao.titulo} onChange={e => setEdicao({ ...edicao, titulo: e.target.value })} />
              </label>
            )}
            <label>{textoAtual?.tipo === 'secao' ? 'Parágrafos (separe com uma linha em branco)' : 'Itens do quadro (separe com uma linha em branco)'}
              <textarea className="input tp-textarea" value={edicao.texto} onChange={e => setEdicao({ ...edicao, texto: e.target.value })} />
            </label>
            <label>Motivo da mudança (fica no histórico)
              <input className="input" value={edicao.motivo} onChange={e => setEdicao({ ...edicao, motivo: e.target.value })} />
            </label>
            <p className="tp-meta">Marcadores: {Object.keys(lista.data?.marcadores || {}).map(m => `{${m}}`).join(' ')}</p>
          </div>
        )}
      </Modal>

      <Modal isOpen={Boolean(reversao)} onClose={() => setReversao(null)} title={`Reverter para a versão ${reversao?.versao}`}
        footer={(
          <div className="tp-rodape">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setReversao(null)}>Cancelar</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={reverter.isPending}
              onClick={() => reverter.mutate({ versao: reversao.versao, motivo: reversao.motivo, regime: sel.regime, tribunal: sel.tribunal })}>
              Reverter
            </button>
          </div>
        )}>
        {reversao && (
          <div className="tp-form">
            <p>O conteúdo da versão {reversao.versao} é publicado de novo como versão nova. Nada é apagado.</p>
            <label>Motivo
              <input className="input" value={reversao.motivo} onChange={e => setReversao({ ...reversao, motivo: e.target.value })} />
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}
